"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"
import { CalendarClockIcon, MenuIcon, SparklesIcon } from "lucide-react"

import type { MenuCategory } from "@workspace/model"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@workspace/ui/components/sheet"

import { ThemeToggle, useCanToggleTheme } from "@/components/theme-toggle"
import { Link } from "@/i18n/navigation"
import { useCategoryHashNav } from "@/components/use-category-hash-nav"
import { categoryIcon } from "@/lib/category-icon"
import { categoryHref, OFFERS_MENU_HREF } from "@/lib/menu-presentation"

/**
 * Mobile-only category drawer for StoreHeader. On viewports below `md` the
 * header's horizontal category quick-nav is hidden; this hamburger opens a Sheet
 * (sliding from the reading-start side — `left` for LTR, `right` for RTL) listing
 * "All menu" plus every category. Each link is wrapped in `SheetClose` so tapping
 * one dismisses the drawer as it navigates. Category links on `/menu` take over
 * the click (`useCategoryHashNav` calls `preventDefault`, which makes Radix's
 * `SheetClose` skip closing), so those close the drawer explicitly via the
 * controlled `open` state. Client component (Sheet needs state);
 * categories/labels are passed down from the server-rendered header.
 */
export function MobileMenuSheet({
  categories,
  side,
  menuLabel,
  allMenuLabel,
  openLabel,
  reservationsEnabled = false,
  hasOffers = false,
  categoryPages = false,
}: {
  categories: MenuCategory[]
  side: "left" | "right"
  menuLabel: string
  allMenuLabel: string
  openLabel: string
  /** Gated same as the header's own "Book a table" link — hidden when the tenant hasn't turned reservations on. */
  reservationsEnabled?: boolean
  /** Some item carries a current offer — adds the "Offers" link (`OFFERS_MENU_HREF`). */
  hasOffers?: boolean
  /** The layout gives each category its own page (`@/lib/menu-presentation`) — link there instead of to a `/menu` section. */
  categoryPages?: boolean
}) {
  const t = useTranslations("nav")
  const canToggleTheme = useCanToggleTheme()
  const onCategoryClick = useCategoryHashNav()
  const [open, setOpen] = useState(false)
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={openLabel}
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-4xl text-foreground hover:bg-muted md:hidden"
        >
          <MenuIcon className="size-5" aria-hidden />
        </button>
      </SheetTrigger>
      <SheetContent side={side} className="w-72 gap-0 p-0">
        <SheetHeader className="border-b">
          <SheetTitle>{menuLabel}</SheetTitle>
        </SheetHeader>
        <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
          <SheetClose asChild>
            <Link
              href="/menu"
              className="inline-flex items-center rounded-lg px-3 py-2.5 font-medium hover:bg-muted"
            >
              {allMenuLabel}
            </Link>
          </SheetClose>
          {hasOffers ? (
            <SheetClose asChild>
              <Link
                href={OFFERS_MENU_HREF}
                className="inline-flex items-center gap-2.5 rounded-lg px-3 py-2.5 hover:bg-muted hover:text-muted-foreground"
              >
                <SparklesIcon className="size-4" aria-hidden />
                {t("offers")}
              </Link>
            </SheetClose>
          ) : null}
          {reservationsEnabled ? (
            <SheetClose asChild>
              <Link
                href="/book"
                className="inline-flex items-center gap-2.5 rounded-lg px-3 py-2.5 hover:bg-muted hover:text-muted-foreground"
              >
                <CalendarClockIcon className="size-4" aria-hidden />
                {t("bookTable")}
              </Link>
            </SheetClose>
          ) : null}
          {categories.map((category) => {
            const Icon = categoryIcon(category)
            return (
              <SheetClose asChild key={category.id}>
                <Link
                  href={categoryHref({ categoryPages }, category.id)}
                  onClick={
                    categoryPages
                      ? undefined
                      : (e) => {
                          onCategoryClick(category.id)(e)
                          setOpen(false)
                        }
                  }
                  className="inline-flex items-center gap-2.5 rounded-lg px-3 py-2.5 hover:bg-muted hover:text-muted-foreground"
                >
                  <Icon className="size-4" aria-hidden />
                  {category.name}
                </Link>
              </SheetClose>
            )
          })}
        </nav>
        {/* Theme toggle — relocated here from the header's main bar on mobile.
            Not wrapped in SheetClose: switching theme keeps the drawer open. */}
        {canToggleTheme ? (
          <SheetFooter className="border-t">
            <div className="flex items-center justify-between px-1">
              <span className="text-sm font-medium">{t("theme")}</span>
              <ThemeToggle />
            </div>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
